'use client';

import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { Icon } from '@/components/ui/icon/icon';
import type { IconName } from '@/components/ui/icon/icon-registry';
import { Spinner } from './spinner';
import styles from './button.module.css';

type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';
type ButtonColor = 'primary' | 'secondary' | 'error' | 'ai';
type ButtonVariant = 'filled' | 'outlined' | 'ghost';
type IconElementSize = 'sm' | 'md' | 'lg';

type ButtonSharedProps = {
  /** Defaults to `small`, the size nearly every page action already used. */
  size?: ButtonSize;
  color?: ButtonColor;
  variant?: ButtonVariant;
  iconStart?: IconName | '';
  iconCenter?: IconName | '';
  iconEnd?: IconName | '';
  iconLabel?: string;
  iconColor?: string;
  isLoading?: boolean;
  round?: boolean;
  gradient?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

// `ref` is declared explicitly on both variants: the *HTMLAttributes types do
// not carry it (JSX supplies it via ClassAttributes, which a plain props type
// does not inherit), so without this a caller cannot hold the rendered element
// — which meant nothing could return focus to a Button. React 19 accepts `ref`
// as an ordinary prop, so the existing spread onto the element is all the
// plumbing needed.
type ButtonAsButton = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'size'> &
  ButtonSharedProps & {
    href?: undefined;
    ref?: Ref<HTMLButtonElement>;
  };

type ButtonAsAnchor = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'size'> &
  ButtonSharedProps & {
    href: string;
    disabled?: boolean;
    ref?: Ref<HTMLAnchorElement>;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const sizeToIconSize: Record<ButtonSize, IconElementSize> = {
  xsmall: 'sm',
  small: 'sm',
  medium: 'sm',
  large: 'md',
};

function renderIcon(
  name: IconName,
  size: IconElementSize,
  iconLabel: string | undefined,
  iconColor: string | undefined,
) {
  return <Icon name={name} size={size} label={iconLabel} color={iconColor} />;
}

function renderChildren(
  children: ReactNode,
  iconSize: IconElementSize,
  isLoading: boolean,
  iconStart: IconName | '',
  iconCenter: IconName | '',
  iconEnd: IconName | '',
  iconLabel: string | undefined,
  iconColor: string | undefined,
) {
  const startIcon = iconStart ? (
    <span className={styles.buttonIconStart}>
      {renderIcon(iconStart, iconSize, iconLabel, iconColor)}
    </span>
  ) : null;

  const endIcon = iconEnd ? (
    <span className={styles.buttonIconEnd}>
      {renderIcon(iconEnd, iconSize, iconLabel, iconColor)}
    </span>
  ) : null;

  if (isLoading) {
    return (
      <span className={styles.buttonTextCenter}>
        <Spinner size={iconSize} />
      </span>
    );
  }

  if (iconCenter) {
    return (
      <>
        {startIcon}
        <span className={styles.buttonIconCenter}>
          {renderIcon(iconCenter, iconSize, iconLabel, iconColor)}
        </span>
        {endIcon}
      </>
    );
  }

  return (
    <>
      {startIcon}
      {/* Omitted, not rendered empty, when there's no label: an icon-only
          button (e.g. the collapsed SearchField trigger, `iconStart` with no
          children) got this span anyway, and `.buttonTextCenter:not(:has(svg))`
          pads any svg-less span — invisible text padding tacked on after the
          icon, widening the button and pushing the icon off-center. */}
      {children ? <span className={styles.buttonTextCenter}>{children}</span> : null}
      {endIcon}
    </>
  );
}

export function Button({
  size = 'small',
  color = 'primary',
  variant = 'filled',
  iconStart = '',
  iconCenter = '',
  iconEnd = '',
  iconLabel,
  iconColor,
  isLoading = false,
  round = false,
  gradient = false,
  className,
  children,
  ...props
}: Readonly<ButtonProps>) {
  const iconSize = sizeToIconSize[size];

  // An icon-only button's accessible name has to live on the BUTTON, not on
  // the Icon inside it. `Icon` renders null until it has fetched its SVG
  // markup in an effect (see the comment in icon/icon.tsx explaining why it
  // cannot render on the first pass), so a name carried by the icon does not
  // exist yet for anyone — screen reader or test — who reaches the button
  // before that fetch settles. Hoisting it makes the name present from the
  // first render and independent of icon loading.
  //
  // Only when there is nothing else to name the button: visible `children`
  // already name it, and an explicit `aria-label` from the caller wins over
  // the icon's own label. In those cases `iconLabel` stays on the icon.
  const explicitAriaLabel = (props as { 'aria-label'?: string })['aria-label'];
  const hoistedLabel = !children && iconLabel && !explicitAriaLabel ? iconLabel : undefined;

  const content = renderChildren(
    children,
    iconSize,
    isLoading,
    iconStart,
    iconCenter,
    iconEnd,
    // Hoisted onto the element below — leaving it here too would nest a
    // role="img" with the same name inside the button that already has it.
    hoistedLabel ? undefined : iconLabel,
    iconColor,
  );

  const resolvedClassName = [
    styles.button,
    `ui-button--${size}`,
    `ui-button--${color}`,
    `ui-button--${variant}`,
    round ? 'ui-button--round' : '',
    gradient ? 'ui-button--gradient' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if ('href' in props && typeof props.href === 'string') {
    const { href, onClick, disabled = false, ...linkProps } = props as ButtonAsAnchor;

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onClick?.(event);
    };

    return (
      <a
        href={href}
        role="button"
        aria-label={hoistedLabel}
        aria-disabled={disabled ? true : undefined}
        tabIndex={disabled ? -1 : linkProps.tabIndex}
        className={`${resolvedClassName}${disabled ? ' disabled' : ''}`}
        onClick={disabled ? undefined : handleClick}
        {...linkProps}
      >
        {content}
      </a>
    );
  }

  const { onClick, disabled = false, type, ...buttonProps } = props as ButtonAsButton;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  return (
    <button
      type={type ?? 'button'}
      aria-label={hoistedLabel}
      disabled={disabled}
      className={`${resolvedClassName}${disabled ? ' disabled' : ''}`}
      onClick={handleClick}
      {...buttonProps}
    >
      {content}
    </button>
  );
}
